<template>
  <v-card class="bitrix-monitor" elevation="0">
    <div class="topbar">
      <div class="left">
        <div class="title">{{ $t('Bitrix Integration Monitor') }}</div>
        <div class="subtitle">
          <v-icon small class="mr-1">mdi-refresh</v-icon>
          {{ $t('Auto-refresh') }}: 15s · {{ nowText }}
        </div>
      </div>
      <div class="right">
        <v-btn small depressed color="primary" outlined :loading="refreshing" @click="refresh">
          <v-icon left small>mdi-reload</v-icon>
          {{ $t('Yangilash') }}
        </v-btn>
      </div>
    </div>

    <div class="royal-grid">
      <div class="royal-card royal--all" :class="{ 'is-active': statusFilter === null }"
           role="button" tabindex="0" @click="filterByStatus(null)" @keyup.enter="filterByStatus(null)">
        <div class="royal-title">{{ $t('Jami') }}</div>
        <div class="royal-count">{{ formatNumber(stats.total) }}</div>
        <div class="royal-meter"><span :style="{ width: '100%' }"></span></div>
      </div>
      <div class="royal-card royal--sent" :class="{ 'is-active': statusFilter === 'SUCCESS' }"
           role="button" tabindex="0" @click="filterByStatus('SUCCESS')" @keyup.enter="filterByStatus('SUCCESS')">
        <div class="royal-title">{{ $t('Yuborilgan') }}</div>
        <div class="royal-count">{{ formatNumber(stats.sent) }}</div>
        <div class="royal-meter"><span :style="{ width: pctOfTotal(stats.sent) }"></span></div>
      </div>
      <div class="royal-card royal--pend" :class="{ 'is-active': statusFilter === 'PENDING' }"
           role="button" tabindex="0" @click="filterByStatus('PENDING')" @keyup.enter="filterByStatus('PENDING')">
        <div class="royal-title">{{ $t('Kutilmoqda') }}</div>
        <div class="royal-count">{{ formatNumber(stats.pending) }}</div>
        <div class="royal-meter"><span :style="{ width: pctOfTotal(stats.pending) }"></span></div>
      </div>
      <div class="royal-card royal--err" :class="{ 'is-active': statusFilter === 'FAILED' }"
           role="button" tabindex="0" @click="filterByStatus('FAILED')" @keyup.enter="filterByStatus('FAILED')">
        <div class="royal-title">{{ $t('Xatolik') }}</div>
        <div class="royal-count">{{ formatNumber(stats.failed) }}</div>
        <div class="royal-meter"><span :style="{ width: pctOfTotal(stats.failed) }"></span></div>
      </div>
      <div class="royal-card royal--today" tabindex="0">
        <div class="royal-title">{{ $t('Bugun yuborilgan') }}</div>
        <div class="royal-count">{{ formatNumber(stats.todaySent) }}</div>
        <div class="royal-meter"><span :style="{ width: pctOfSent(stats.todaySent) }"></span></div>
      </div>
    </div>

    <div class="progress-card">
      <div class="progress-head">
        <div class="progress-title">{{ $t('Umumiy progress') }}</div>
        <div class="progress-percent">{{ progressPercent }}%</div>
      </div>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progressPercent + '%' }">
          <span class="progress-shimmer"></span>
        </div>
      </div>
      <div class="progress-foot">
        <div>{{ formatNumber(stats.sent) }} / {{ formatNumber(stats.total) }} {{ $t('yuborilgan') }}</div>
        <div>{{ $t("Oxirgi yuborish") }}: {{ formatDate(stats.lastSentAt) || '—' }}</div>
      </div>
    </div>

    <div class="activity-card">
      <div class="activity-head">
        <div class="activity-title-wrap">
          <div class="activity-title">{{ $t("Oxirgi faoliyat") }}</div>
          <div v-if="totalEvents > 0" class="activity-range">
            {{ formatNumber(rangeStart) }}–{{ formatNumber(rangeEnd) }} / {{ formatNumber(totalEvents) }}
          </div>
          <button v-if="statusFilter" type="button" class="filter-chip" @click="filterByStatus(null)">
            {{ statusLabel(statusFilter) }}
            <span class="filter-chip-x">✕</span>
          </button>
        </div>

        <div class="activity-tools">
          <div class="search-box search-box--ariza" :class="{ 'search-box--active': arizaSearch }">
            <v-icon small class="search-box__icon">mdi-magnify</v-icon>
            <input
              type="text"
              class="search-box__input"
              inputmode="numeric"
              autocomplete="off"
              :placeholder="$t('Ariza raqami bo\'yicha qidirish')"
              v-model="arizaSearch"
              @input="onArizaInput"
              @keyup.enter="applyArizaSearch"
            />
            <button
              v-if="arizaSearch"
              type="button"
              class="search-box__clear"
              :title="$t('Tozalash')"
              @click="clearArizaSearch"
            >✕</button>
          </div>
          <div class="search-box" :class="{ 'search-box--active': phoneSearch }">
            <v-icon small class="search-box__icon">mdi-magnify</v-icon>
            <input
              type="text"
              class="search-box__input"
              inputmode="tel"
              autocomplete="off"
              :placeholder="$t('Telefon raqami bo\'yicha qidirish')"
              v-model="phoneSearch"
              @input="onPhoneInput"
              @keyup.enter="applyPhoneSearch"
            />
            <button
              v-if="phoneSearch"
              type="button"
              class="search-box__clear"
              :title="$t('Tozalash')"
              @click="clearPhoneSearch"
            >✕</button>
          </div>

          <div v-if="selected.length" class="selection-bar">
            <span class="selection-count">{{ selected.length }} {{ $t('tanlangan') }}</span>
            <v-btn x-small text @click="clearSelection">{{ $t('Bekor qilish') }}</v-btn>
            <v-btn v-if="false" x-small depressed color="error" @click="confirmDelete">
              <v-icon left x-small>mdi-trash-can-outline</v-icon>
              {{ $t("O'chirish") }} ({{ selected.length }})
            </v-btn>
          </div>
        </div>
      </div>

      <div class="activity-table-wrap">
        <table class="activity-table">
          <thead>
            <tr>
              <th class="col-check" @click.stop>
                <v-simple-checkbox
                  :ripple="false"
                  :value="allSelected"
                  :indeterminate="someSelected && !allSelected"
                  @input="toggleAll"
                />
              </th>
              <th>{{ $t('Voqea') }}</th>
              <th>{{ $t('Holat') }}</th>
              <th>{{ $t('Ariza') }}</th>
              <th>{{ $t('Bitrix ID') }}</th>
              <th>{{ $t('Urinishlar') }}</th>
              <th>{{ $t('Vaqt') }}</th>
              <th>{{ $t('Xato') }}</th>
              <th class="col-arrow"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!recent.length && !loading">
              <td colspan="9" class="empty-row">{{ $t('No data') }}</td>
            </tr>
            <tr
              v-for="row in recent"
              :key="row.id"
              class="clickable"
              :class="{ 'is-selected': isSelected(row.id) }"
              @click="openDetails(row)"
            >
              <td class="col-check" @click.stop>
                <v-simple-checkbox
                  :ripple="false"
                  :value="isSelected(row.id)"
                  @input="toggleOne(row.id)"
                />
              </td>
              <td><span class="event-chip">{{ row.eventType }}</span></td>
              <td>
                <span class="status-chip" :class="statusClass(row.status)">
                  <span class="status-dot"></span>
                  {{ statusLabel(row.status) }}
                </span>
              </td>
              <td>
                <button
                  v-if="row.applicationId"
                  type="button"
                  class="ariza-badge"
                  :title="$t('Nusxa olish')"
                  @click.stop="copy(row.applicationId)"
                >
                  <span class="mono">{{ row.applicationId }}</span>
                  <v-icon x-small class="ariza-badge__copy">mdi-content-copy</v-icon>
                </button>
                <span v-else class="mono">—</span>
              </td>
              <td class="mono">{{ row.bitrixEntityId || '—' }}</td>
              <td class="mono">{{ row.retryCount != null ? row.retryCount : 0 }}</td>
              <td class="mono">{{ formatDate(row.createdAt) }}</td>
              <td class="error-cell">
                <span v-if="row.errorMessage" :title="row.errorMessage">
                  {{ truncate(row.errorMessage, 80) }}
                </span>
                <span v-else>—</span>
              </td>
              <td class="col-arrow"><span aria-hidden="true">→</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalPages > 0" class="pagination">
        <div class="pagination__size">
          <span class="pagination__label">{{ $t('Sahifada') }}</span>
          <select
            class="pagination__select"
            :value="pageSize"
            @change="e => onPageSizeChange(e.target.value)"
          >
            <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <div class="pagination__nav">
          <button
            class="pager-btn"
            :disabled="pageNum === 0"
            @click="goToPage(0)"
            :title="$t('Birinchi')"
          >«</button>
          <button
            class="pager-btn"
            :disabled="pageNum === 0"
            @click="goToPage(pageNum - 1)"
            :title="$t('Oldingi')"
          >‹</button>

          <template v-for="(p, idx) in pageNumbers">
            <span v-if="p === '…'" :key="'gap-' + idx" class="pager-gap">…</span>
            <button
              v-else
              :key="p"
              class="pager-btn pager-num"
              :class="{ 'pager-num--active': p === pageNum }"
              @click="goToPage(p)"
            >{{ p + 1 }}</button>
          </template>

          <button
            class="pager-btn"
            :disabled="pageNum >= totalPages - 1"
            @click="goToPage(pageNum + 1)"
            :title="$t('Keyingi')"
          >›</button>
          <button
            class="pager-btn"
            :disabled="pageNum >= totalPages - 1"
            @click="goToPage(totalPages - 1)"
            :title="$t('Oxirgi')"
          >»</button>
        </div>
      </div>
    </div>

    <v-dialog v-model="confirmDialog" max-width="420px">
      <v-card class="confirm-card">
        <v-card-title class="confirm-title">{{ $t("O'chirishni tasdiqlang") }}</v-card-title>
        <v-card-text class="confirm-body">
          {{ selected.length }} {{ $t("ta yozuvni o'chirmoqchimisiz?") }}
          {{ $t("Bu amalni qaytarib bo'lmaydi.") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">{{ $t('Bekor qilish') }}</v-btn>
          <v-btn depressed color="error" :loading="deleting" @click="deleteSelected">
            <v-icon left small>mdi-trash-can-outline</v-icon>
            {{ $t("O'chirish") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resendDialog" max-width="480px">
      <v-card class="confirm-card">
        <v-card-title class="confirm-title">{{ $t("Qayta yuborishni tasdiqlang") }}</v-card-title>
        <v-card-text class="confirm-body">
          {{ formatNumber(stats.failed) }} {{ $t("ta xato so'rov qayta yuboriladi.") }}
          {{ $t("Har bir talaba uchun so'rovlar navbat bilan, ketma-ket qayta yuboriladi.") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="resendDialog = false">{{ $t('Bekor qilish') }}</v-btn>
          <v-btn depressed color="info" :loading="resending" @click="resendErrors">
            <v-icon left small>mdi-refresh-circle</v-icon>
            {{ $t("Qayta yuborish") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="900px" scrollable>
      <v-card v-if="details" class="details-dialog">
        <v-card-title class="details-title">
          <span class="event-chip">{{ details.eventType }}</span>
          <span class="status-chip ml-2" :class="statusClass(details.status)">
            <span class="status-dot"></span>
            {{ statusLabel(details.status) }}
          </span>
          <v-spacer />
          <v-btn icon @click="detailsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="details-body">
          <div class="meta-grid">
            <div><span class="meta-label">ID</span><span class="meta-value mono">{{ details.id }}</span></div>
            <div><span class="meta-label">{{ $t('Vaqt') }}</span><span class="meta-value mono">{{ formatDate(details.createdAt) }}</span></div>
            <div><span class="meta-label">{{ $t('Ariza') }}</span><span class="meta-value mono">{{ details.applicationId || '—' }}</span></div>
            <div><span class="meta-label">Member ID</span><span class="meta-value mono">{{ details.memberId || '—' }}</span></div>
            <div><span class="meta-label">{{ $t('Bitrix ID') }}</span><span class="meta-value mono">{{ details.bitrixEntityId || '—' }}</span></div>
            <div><span class="meta-label">{{ $t('Urinishlar') }}</span><span class="meta-value mono">{{ details.retryCount != null ? details.retryCount : 0 }}</span></div>
          </div>

          <div v-if="details.errorMessage" class="block">
            <div class="block-head">
              <span class="block-title block-title--error">{{ $t('Xato') }}</span>
            </div>
            <pre class="code code--error">{{ details.errorMessage }}</pre>
          </div>

          <div class="block">
            <div class="block-head">
              <span class="block-title">{{ $t('Sorov tanasi') }}</span>
              <v-btn x-small text @click="copy(details.requestBody)" :disabled="!details.requestBody">
                <v-icon left x-small>mdi-content-copy</v-icon>{{ $t('Nusxa olish') }}
              </v-btn>
            </div>
            <div v-if="endpointsForDetail.length" class="endpoint-row">
              <span
                v-for="ep in endpointsForDetail"
                :key="ep.url"
                class="endpoint"
                :title="ep.url"
                @click="copy(ep.url)"
              >
                <span class="endpoint__verb">{{ ep.method }}</span>
                <span class="endpoint__url">{{ ep.url }}</span>
              </span>
            </div>
            <pre class="code">{{ formatJson(details.requestBody) || '—' }}</pre>
          </div>

          <div class="block">
            <div class="block-head">
              <span class="block-title">{{ $t('Javob tanasi') }}</span>
              <v-btn x-small text @click="copy(details.responseBody)" :disabled="!details.responseBody">
                <v-icon left x-small>mdi-content-copy</v-icon>{{ $t('Nusxa olish') }}
              </v-btn>
            </div>
            <pre class="code">{{ formatJson(details.responseBody) || '—' }}</pre>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="2200" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>

<script>
import axios from "axios";

export default {
  name: "AdminBitrixMonitor",
  data: () => ({
    stats: { total: 0, sent: 0, pending: 0, failed: 0, todaySent: 0, lastSentAt: null },
    recent: [],
    pageNum: 0,
    pageSize: 25,
    totalEvents: 0,
    totalPages: 0,
    statusFilter: null, // null = all; 'FAILED' | 'SUCCESS' | 'PENDING' filters the activity list
    phoneSearch: "", // filters the activity list to one student's events by their phone number
    searchDebounce: null,
    arizaSearch: "", // filters the activity list to one application's events by its ariza (application id)
    arizaDebounce: null,
    pageSizeOptions: [10, 25, 50, 100],
    loading: false,
    refreshing: false,
    pollHandle: null,
    nowText: "",
    detailsDialog: false,
    details: null,
    snackbar: { show: false, text: "" },
    selected: [],
    confirmDialog: false,
    deleting: false,
    resendDialog: false,
    resending: false,
  }),
  computed: {
    progressPercent() {
      if (!this.stats.total) return 0;
      return Math.round((this.stats.sent / this.stats.total) * 100);
    },
    endpointsForDetail() {
      if (!this.details) return [];
      // Prefer the real URL captured at request time
      if (this.details.requestUrl) {
        return [{ method: this.details.requestMethod || "POST", url: this.details.requestUrl }];
      }
      // Fallback for legacy rows written before request_url was tracked
      return this.apiEndpoints(this.details.eventType).map((url) => ({ method: "POST", url }));
    },
    allSelected() {
      return this.recent.length > 0 && this.selected.length === this.recent.length;
    },
    someSelected() {
      return this.selected.length > 0;
    },
    rangeStart() {
      if (this.totalEvents === 0) return 0;
      return this.pageNum * this.pageSize + 1;
    },
    rangeEnd() {
      return Math.min((this.pageNum + 1) * this.pageSize, this.totalEvents);
    },
    pageNumbers() {
      // Compact pager: first, last, current ±2, with '…' gaps. Always returns
      // an array of either page-index numbers (0-based) or the string '…'.
      const total = this.totalPages;
      const cur = this.pageNum;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i);
      const pages = new Set([0, total - 1, cur, cur - 1, cur + 1, cur - 2, cur + 2]);
      const sorted = [...pages].filter(p => p >= 0 && p < total).sort((a, b) => a - b);
      const out = [];
      let prev = -1;
      for (const p of sorted) {
        if (prev !== -1 && p - prev > 1) out.push("…");
        out.push(p);
        prev = p;
      }
      return out;
    },
  },
  created() {
    this.fetchAll(true);
  },
  mounted() {
    this.pollHandle = setInterval(() => this.fetchAll(false), 15000);
  },
  beforeDestroy() {
    if (this.pollHandle) clearInterval(this.pollHandle);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    if (this.arizaDebounce) clearTimeout(this.arizaDebounce);
  },
  methods: {
    async fetchAll(initial) {
      if (initial) this.loading = true;
      this.refreshing = !initial;
      try {
        const [statsRes, pageRes] = await Promise.all([
          axios.get("/bitrix-monitor/stats"),
          axios.get("/bitrix-monitor/events", {
            params: {
              page: this.pageNum,
              size: this.pageSize,
              ...(this.statusFilter ? { status: this.statusFilter } : {}),
              ...(this.phoneSearch && this.phoneSearch.trim() ? { phone: this.phoneSearch.trim() } : {}),
              ...(String(this.arizaSearch || "").replace(/\D/g, "") ? { applicationId: String(this.arizaSearch).replace(/\D/g, "") } : {}),
            },
          }),
        ]);
        this.stats = statsRes.data;
        const data = pageRes.data || {};
        this.recent = data.content || [];
        this.totalEvents = Number(data.total) || 0;
        this.totalPages = Number(data.totalPages) || 0;
        // If the current page no longer exists (e.g. after bulk delete), snap back.
        if (this.totalPages > 0 && this.pageNum >= this.totalPages) {
          this.pageNum = this.totalPages - 1;
          return this.fetchAll(false);
        }
        this.nowText = new Date().toLocaleTimeString();
      } catch (e) {
        console.error("Failed to load Bitrix monitor", e);
      } finally {
        this.loading = false;
        this.refreshing = false;
      }
    },
    refresh() {
      return this.fetchAll(false);
    },
    goToPage(p) {
      if (typeof p !== "number" || p < 0 || p >= this.totalPages || p === this.pageNum) return;
      this.pageNum = p;
      this.selected = [];
      this.fetchAll(false);
    },
    onPageSizeChange(size) {
      this.pageSize = Number(size) || 25;
      this.pageNum = 0;
      this.selected = [];
      this.fetchAll(false);
    },
    filterByStatus(status) {
      // Click a status card to show only those rows; click it again (or "Jami") to clear.
      this.statusFilter = this.statusFilter === status ? null : status;
      this.pageNum = 0;
      this.selected = [];
      this.fetchAll(false);
    },
    onPhoneInput() {
      // Debounce so we query once the user pauses typing, not on every keystroke.
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.applyPhoneSearch(), 400);
    },
    applyPhoneSearch() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      // Reset to the first page — the filtered result set has its own pagination.
      this.pageNum = 0;
      this.selected = [];
      this.fetchAll(false);
    },
    clearPhoneSearch() {
      if (!this.phoneSearch) return;
      this.phoneSearch = "";
      this.applyPhoneSearch();
    },
    onArizaInput() {
      // Debounce so we query once the user pauses typing, not on every keystroke.
      if (this.arizaDebounce) clearTimeout(this.arizaDebounce);
      this.arizaDebounce = setTimeout(() => this.applyArizaSearch(), 400);
    },
    applyArizaSearch() {
      if (this.arizaDebounce) {
        clearTimeout(this.arizaDebounce);
        this.arizaDebounce = null;
      }
      // Reset to the first page — the filtered result set has its own pagination.
      this.pageNum = 0;
      this.selected = [];
      this.fetchAll(false);
    },
    clearArizaSearch() {
      if (!this.arizaSearch) return;
      this.arizaSearch = "";
      this.applyArizaSearch();
    },
    pctOfTotal(n) {
      const t = Number(this.stats.total) || 0;
      if (!t) return "0%";
      return Math.min(100, Math.round((Number(n) || 0) / t * 100)) + "%";
    },
    pctOfSent(n) {
      const s = Number(this.stats.sent) || 0;
      if (!s) return "0%";
      return Math.min(100, Math.round((Number(n) || 0) / s * 100)) + "%";
    },
    formatNumber(n) {
      if (n == null) return 0;
      return Number(n).toLocaleString("en-US");
    },
    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const p2 = (n) => (n < 10 ? "0" + n : "" + n);
      return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
    },
    truncate(s, n) {
      return s && s.length > n ? s.slice(0, n) + "…" : s;
    },
    statusClass(status) {
      if (status === "SUCCESS") return "status--success";
      if (status === "FAILED") return "status--failed";
      return "status--pending";
    },
    statusLabel(status) {
      if (status === "SUCCESS") return this.$t("Yuborilgan");
      if (status === "FAILED") return this.$t("Xatolik");
      return this.$t("Kutilmoqda");
    },
    async openDetails(row) {
      this.details = row;
      this.detailsDialog = true;
      try {
        const { data } = await axios.get(`/bitrix-monitor/${row.id}`);
        this.details = data;
      } catch (e) {
        console.error("Failed to load Bitrix log details", e);
      }
    },
    formatJson(text) {
      if (!text) return "";
      try {
        return JSON.stringify(JSON.parse(text), null, 2);
      } catch (e) {
        return text;
      }
    },
    apiEndpoints(eventType) {
      const base = "https://crm.usatportal.uz/rest/462/fxuekr3yio31a3ef";
      const map = {
        CONTACT_CREATE:           ["crm.contact.add.json"],
        DEAL_CREATE:              ["crm.deal.add.json"],
        DEAL_UPDATE_PERSONAL:     ["crm.deal.update.json"],
        DEAL_UPDATE_EDUCATION:    ["crm.deal.update.json"],
        DEAL_UPDATE_CITIZENSHIP:  ["crm.deal.update.json"],
        DEAL_UPDATE_SCHOOL:       ["crm.deal.update.json"],
        DEAL_UPDATE_CONTACT_INFO: ["crm.deal.update.json"],
        DEAL_UPDATE_EXAM_RESULT:  ["crm.deal.update.json"],
        DEAL_UPDATE_ACCEPTED:     ["crm.deal.update.json"],
        DEAL_UPDATE_REJECTED:     ["crm.deal.update.json"],
        DEAL_UPDATE_PAYMENT:      ["crm.deal.update.json"],
        APPLICATION_DELETE:       ["crm.contact.delete.json", "crm.deal.delete.json"],
      };
      const paths = map[eventType] || [];
      return paths.map((p) => `${base}/${p}`);
    },
    copy(text) {
      if (!text) return;
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      this.snackbar.text = this.$t("Nusxa olindi");
      this.snackbar.show = true;
    },
    isSelected(id) {
      return this.selected.indexOf(id) !== -1;
    },
    toggleOne(id) {
      const i = this.selected.indexOf(id);
      if (i === -1) this.selected.push(id);
      else this.selected.splice(i, 1);
    },
    toggleAll() {
      if (this.allSelected) this.selected = [];
      else this.selected = this.recent.map((r) => r.id);
    },
    clearSelection() {
      this.selected = [];
    },
    confirmDelete() {
      if (!this.selected.length) return;
      this.confirmDialog = true;
    },
    async deleteSelected() {
      if (!this.selected.length) return;
      this.deleting = true;
      try {
        const ids = this.selected.slice();
        await axios.delete("/bitrix-monitor", { data: { ids } });
        this.snackbar.text = ids.length + " " + this.$t("yozuv o'chirildi");
        this.snackbar.show = true;
        this.selected = [];
        this.confirmDialog = false;
        await this.fetchAll(false);
      } catch (e) {
        console.error("Failed to delete Bitrix logs", e);
        this.snackbar.text = this.$t("O'chirishda xatolik");
        this.snackbar.show = true;
      } finally {
        this.deleting = false;
      }
    },
    confirmResend() {
      if (!this.stats.failed) return;
      this.resendDialog = true;
    },
    async resendErrors() {
      this.resending = true;
      try {
        const { data } = await axios.post("/bitrix-monitor/resend", { all: true });
        const queued = data && data.queued != null ? data.queued : 0;
        this.snackbar.text = queued + " " + this.$t("ta so'rov navbatga qo'shildi");
        this.snackbar.show = true;
        this.resendDialog = false;
        await this.fetchAll(false);
      } catch (e) {
        console.error("Failed to queue Bitrix resend", e);
        this.snackbar.text = this.$t("Qayta yuborishda xatolik");
        this.snackbar.show = true;
      } finally {
        this.resending = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.bitrix-monitor {
  padding: 24px;
  background: transparent;
}

/* Clickable status cards (click a card to filter the activity list below) */
.royal-card[role="button"] {
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.royal-card[role="button"]:hover {
  transform: translateY(-2px);
}
.royal-card.is-active {
  box-shadow: 0 0 0 2px rgba(91, 108, 255, 0.55), 0 8px 22px rgba(0, 0, 0, 0.12);
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
  padding: 2px 10px;
  border-radius: 999px;
  border: 1px solid rgba(120, 120, 140, 0.35);
  background: rgba(120, 120, 140, 0.1);
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.filter-chip-x {
  font-size: 11px;
  opacity: 0.7;
}

.topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;

  .title {
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -.01em;
  }
  .subtitle {
    margin-top: 6px;
    font-size: 13px;
    color: #6b7280;
    display: flex;
    align-items: center;
  }
}

/* ─── stats ─── */
/* Royal-card stats — same look as /applications page */
$royal-line:    #e6e8ec;
$royal-ink:     #0f172a;
$royal-muted:   #667085;
$royal-blue:    #3451e6;
$royal-amber:   #e67e22;
$royal-red:     #c81e1e;
$royal-emerald: #148b4b;
$royal-teal:    #0d9488;

.royal-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 720px)  { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px)  { grid-template-columns: 1fr; }
}

.royal-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  border: 1px solid $royal-line;
  border-radius: 14px;
  padding: 16px;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  transition: border-color .18s ease, transform .18s ease;
  outline: none;
  animation: royal-rise .38s ease both;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(0,0,0,.03), transparent);
    transform: translateX(-100%);
    pointer-events: none;
  }
  &:hover {
    transform: translateY(-1px);
    border-color: #d7d9df;
  }
  &:hover::after { animation: royal-wave 1200ms ease; }
  &:active { transform: translateY(0); }
  &:focus  { outline: none; }

  &:nth-child(1) { animation-delay: 40ms; }
  &:nth-child(2) { animation-delay: 80ms; }
  &:nth-child(3) { animation-delay: 120ms; }
  &:nth-child(4) { animation-delay: 160ms; }
  &:nth-child(5) { animation-delay: 200ms; }
}

.royal-title {
  color: $royal-muted;
  font-size: 11px;
  line-height: 1;
  letter-spacing: .08em;
  text-transform: uppercase;
  font-weight: 700;
}
.royal-count {
  color: $royal-ink;
  font-weight: 900;
  font-size: clamp(24px, 3.6vw, 36px);
  line-height: 1;
  letter-spacing: -.2px;
  font-variant-numeric: tabular-nums lining-nums;
}
.royal-meter {
  height: 6px;
  margin-top: 6px;
  border-radius: 999px;
  background: #f1f3f5;
  overflow: hidden;
  > span {
    display: block;
    height: 100%;
    width: 0%;
    transition: width .45s cubic-bezier(.2,.8,.2,1);
  }
}

.royal-card.royal--all   { .royal-meter > span { background: $royal-blue;    } &.is-active { box-shadow: 0 0 0 2px $royal-blue,    0 8px 22px rgba(0, 0, 0, .12); } }
.royal-card.royal--sent  { .royal-meter > span { background: $royal-emerald; } &.is-active { box-shadow: 0 0 0 2px $royal-emerald, 0 8px 22px rgba(0, 0, 0, .12); } }
.royal-card.royal--pend  { .royal-meter > span { background: $royal-amber;   } &.is-active { box-shadow: 0 0 0 2px $royal-amber,   0 8px 22px rgba(0, 0, 0, .12); } }
.royal-card.royal--err   { .royal-meter > span { background: $royal-red;     } &.is-active { box-shadow: 0 0 0 2px $royal-red,     0 8px 22px rgba(0, 0, 0, .12); } }
.royal-card.royal--today { .royal-meter > span { background: $royal-teal;    } &.is-active { box-shadow: 0 0 0 2px $royal-teal,    0 8px 22px rgba(0, 0, 0, .12); } }

@keyframes royal-rise {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes royal-wave {
  from { transform: translateX(-100%); }
  to   { transform: translateX(100%); }
}

@keyframes bx-rise {
  to { opacity: 1; transform: translateY(0); }
}

/* ─── progress ─── */
.progress-card {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 2px rgba(17, 24, 39, .04);

  .progress-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 14px;
  }
  .progress-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
  .progress-percent {
    font-size: 22px;
    font-weight: 700;
    color: #6366f1;
    font-feature-settings: "tnum";
    letter-spacing: -.01em;
  }
  .progress-track {
    height: 8px;
    background: #eef2ff;
    border-radius: 999px;
    overflow: hidden;
    box-shadow: inset 0 1px 0 rgba(17, 24, 39, .03);
  }
  .progress-fill {
    position: relative;
    height: 100%;
    background: linear-gradient(90deg, #6366f1, #3b82f6);
    border-radius: 999px;
    transition: width .8s cubic-bezier(.2,.8,.2,1);
    overflow: hidden;
  }
  .progress-shimmer {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.55) 50%, transparent 100%);
    transform: translateX(-100%);
    animation: bx-shimmer 2.4s ease-in-out infinite;
  }
  .progress-foot {
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: #6b7280;
  }
}

@keyframes bx-shimmer {
  to { transform: translateX(100%); }
}

/* ─── activity ─── */
.activity-card {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 2px rgba(17, 24, 39, .04);
}

.activity-head {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  .activity-title-wrap {
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
  }
  .activity-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
  .activity-range {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
  }
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 4px 4px;
  flex-wrap: wrap;

  &__size {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  &__label {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
  }
  &__select {
    appearance: none;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 6px 28px 6px 10px;
    font-size: 13px;
    font-weight: 500;
    color: #111827;
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    cursor: pointer;
    transition: border-color .15s ease, box-shadow .15s ease;
    &:hover { border-color: #c7d2fe; }
    &:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.12); }
  }
  &__nav {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

.pager-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: all .15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    border-color: #c7d2fe;
    background: #f5f3ff;
    color: #4338ca;
  }
  &:disabled {
    opacity: .4;
    cursor: not-allowed;
  }
  &.pager-num--active {
    border-color: #6366f1;
    background: #6366f1;
    color: #fff;
    cursor: default;
  }
  &.pager-num--active:hover {
    background: #6366f1;
    color: #fff;
  }
}
.pager-gap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 32px;
  color: #9ca3af;
  font-weight: 600;
  user-select: none;
}

.selection-bar {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px 4px 14px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid rgba(99, 102, 241, .2);
  animation: bx-pop .25s ease-out;
}
.selection-count {
  font-size: 13px;
  font-weight: 600;
  color: #4338ca;
  letter-spacing: -.01em;
}
@keyframes bx-pop {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Right-hand cluster of the activity header: phone search + bulk-selection bar. */
.activity-tools {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.search-box {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  min-width: 248px;
  padding: 0 8px 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:hover { border-color: #c7d2fe; }
  &:focus-within {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, .12);
  }
  &--active { border-color: #c7d2fe; }
  &--ariza { min-width: 190px; } // ariza (application id) is short, so it needs less room than a phone

  &__icon {
    color: #9ca3af !important;
    flex-shrink: 0;
  }
  &__input {
    flex: 1 1 auto;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #111827;
    &::placeholder { color: #9ca3af; }
  }
  &__clear {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 999px;
    background: #f1f5f9;
    color: #6b7280;
    font-size: 11px;
    line-height: 1;
    cursor: pointer;
    transition: background .15s ease, color .15s ease;
    &:hover { background: #e2e8f0; color: #ef4444; }
  }
}

// Copyable Ariza (application id) chip in the activity table — click to copy the id.
.ariza-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
  color: #111827;
  cursor: pointer;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;

  &:hover {
    border-color: #c7d2fe;
    background: #eef2ff;
  }
  &:active { box-shadow: 0 0 0 3px rgba(99, 102, 241, .12); }

  .mono { font-size: 13px; }

  &__copy {
    color: #9ca3af !important;
    flex-shrink: 0;
    transition: color .15s ease;
  }
  &:hover &__copy { color: #6366f1 !important; }
}

@media (max-width: 640px) {
  .activity-tools { width: 100%; }
  .search-box { width: 100%; min-width: 0; }
}

.activity-table-wrap { overflow-x: auto; }

.activity-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  tr.clickable {
    cursor: pointer;
    position: relative;
  }
  tr.clickable td {
    transition: background .18s ease;
  }
  tr.clickable td:first-child {
    box-shadow: inset 0 0 0 0 #6366f1;
    transition: box-shadow .18s ease, background .18s ease;
  }
  tr.clickable:hover td { background: #f8fafc; }
  tr.clickable:hover td:first-child { box-shadow: inset 3px 0 0 0 #6366f1; }
  tr.clickable:hover .col-arrow span { transform: translateX(3px); color: #6366f1; }

  th, td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid #f1f5f9;
  }
  th {
    font-weight: 600;
    color: #6b7280;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: .03em;
    background: #fafbfc;
  }
  td {
    color: #111827;
  }
  tbody tr:last-child td { border-bottom: none; }

  .empty-row {
    text-align: center;
    color: #9ca3af;
    padding: 32px 0;
  }
  .error-cell {
    max-width: 280px;
    color: #b91c1c;
    font-size: 12px;
  }
  .col-arrow {
    width: 28px;
    text-align: right;
    color: #cbd5e1;
    span {
      display: inline-block;
      transition: transform .2s ease, color .2s ease;
    }
  }

  .col-check {
    width: 32px;
    padding-right: 0 !important;
  }

  tr.is-selected td {
    background: #eef2ff;
  }
  tr.is-selected:hover td {
    background: #e0e7ff;
  }
}

/* Checkbox column — keep the v-simple-checkbox compact and aligned. */
.col-check ::v-deep .v-simple-checkbox {
  display: inline-flex;
  align-items: center;
}
.col-check ::v-deep .v-simple-checkbox .v-input--selection-controls__input {
  margin: 0;
}

/* ─── chips ─── */
.event-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .01em;
  border: 1px solid rgba(99, 102, 241, .12);
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 0 2px rgba(255,255,255,.6);
}
.status--success { background: #ecfdf5; color: #047857; border-color: rgba(4, 120, 87, .15); }
.status--failed  { background: #fef2f2; color: #b91c1c; border-color: rgba(185, 28, 28, .15); }
.status--pending { background: #fffbeb; color: #b45309; border-color: rgba(180, 83, 9, .15); }

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-feature-settings: "tnum", "zero";
}

.confirm-card {
  border-radius: 14px !important;
  .confirm-title {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
    padding: 18px 20px 6px;
  }
  .confirm-body {
    color: #4b5563;
    font-size: 14px;
    line-height: 1.55;
    padding: 0 20px 12px;
  }
}

/* ─── dialog ─── */
.details-dialog {
  border-radius: 16px !important;

  .details-title {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
  }
  .details-body {
    padding: 20px;
  }
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px 24px;
  margin-bottom: 18px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #eef0f4;
  border-radius: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
  .meta-label {
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #6b7280;
    margin-bottom: 2px;
  }
  .meta-value {
    display: block;
    font-size: 14px;
    color: #111827;
    word-break: break-all;
  }
}

.block {
  margin-top: 16px;
  .block-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .block-title {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }
  .block-title--error {
    color: #b91c1c;
  }
  .endpoint-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0 0 8px;
  }
  .endpoint {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px 6px 6px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fafbff;
    font: 500 12px/1 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: #1e293b;
    cursor: pointer;
    transition: background .15s ease, border-color .15s ease;
    max-width: 100%;
  }
  .endpoint:hover {
    background: #f1f5ff;
    border-color: #c7d2fe;
  }
  .endpoint__verb {
    display: inline-flex;
    align-items: center;
    padding: 3px 7px;
    border-radius: 5px;
    background: #6366f1;
    color: #fff;
    font-weight: 700;
    font-size: 10px;
    letter-spacing: .04em;
  }
  .endpoint__url {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .code {
    background: #0b1024;
    color: #e6e9ff;
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 12.5px;
    line-height: 1.65;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    overflow: auto;
    max-height: 320px;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, .04);
  }
  .code--error {
    background: #fef2f2;
    color: #b91c1c;
    max-height: 220px;
    box-shadow: inset 3px 0 0 0 #ef4444;
  }
}
@media (max-width: 600px) {
  .topbar { flex-direction: column; align-items: stretch; gap: 12px; }
  .topbar .right { display: flex; flex-wrap: wrap; gap: 8px; }
}

/* ══════════════════════════════════════════════════════════════════
   DARK MODE  ·  .theme--dark overrides (Vuetify puts .theme--dark on
   the .v-application root). Everything above is untouched light mode.
   Palette — text #e4e5eb · muted #8a8fa3 · panel #1a1b23 ·
   accent #22232e · hover #2a2c3a · border #2a2c3a / #33354a ·
   placeholder #606275. Accent hues (blue/emerald/amber/red/teal
   meters, purple %, indigo chips, green status) are preserved.
   ══════════════════════════════════════════════════════════════════ */

/* ── topbar ── */
.theme--dark .topbar .title    { color: #e4e5eb; }
.theme--dark .topbar .subtitle { color: #8a8fa3; }
.theme--dark .filter-chip {
  border-color: rgba(255, 255, 255, .18);
  background: rgba(255, 255, 255, .06);
  color: #e4e5eb;
}

/* ── royal stat cards ── */
.theme--dark .royal-card {
  background: #1a1b23;
  border-color: #2a2c3a;
}
.theme--dark .royal-card::after {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .05), transparent);
}
.theme--dark .royal-card:hover { border-color: #33354a; }
.theme--dark .royal-title { color: #8a8fa3; }
.theme--dark .royal-count { color: #e4e5eb; }
.theme--dark .royal-meter { background: #22232e; }

/* ── progress card ── */
.theme--dark .progress-card {
  background: #1a1b23;
  border-color: #2a2c3a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .35);
}
.theme--dark .progress-card .progress-title { color: #e4e5eb; }
.theme--dark .progress-card .progress-track {
  background: #22232e;
  box-shadow: inset 0 1px 0 rgba(0, 0, 0, .25);
}
.theme--dark .progress-card .progress-foot { color: #8a8fa3; }

/* ── activity card ── */
.theme--dark .activity-card {
  background: #1a1b23;
  border-color: #2a2c3a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .35);
}
.theme--dark .activity-head .activity-title { color: #e4e5eb; }
.theme--dark .activity-head .activity-range { color: #8a8fa3; }

/* ── phone search box ── */
.theme--dark .search-box {
  background: #22232e;
  border-color: #33354a;
}
.theme--dark .search-box__input { color: #e4e5eb; }
.theme--dark .search-box__input::placeholder { color: #606275; }
.theme--dark .search-box__clear {
  background: #2a2c3a;
  color: #8a8fa3;
}
.theme--dark .search-box__clear:hover { background: #33354a; color: #ef4444; }

/* ── copyable Ariza chip ── */
.theme--dark .ariza-badge {
  background: #22232e;
  border-color: #33354a;
  color: #e4e5eb;
}
.theme--dark .ariza-badge:hover {
  background: #2a2c3a;
  border-color: #4b4e6b;
}
.theme--dark .ariza-badge__copy { color: #8a8fa3 !important; }
.theme--dark .ariza-badge:hover .ariza-badge__copy { color: #c7d2fe !important; }

/* ── bulk-selection bar ── */
.theme--dark .selection-bar {
  background: rgba(99, 102, 241, .16);
  border-color: rgba(99, 102, 241, .35);
}
.theme--dark .selection-count { color: #c7d2fe; }

/* ── pagination ── */
.theme--dark .pagination__label { color: #8a8fa3; }
.theme--dark .pagination__select {
  background-color: #22232e;
  border-color: #33354a;
  color: #e4e5eb;
}
.theme--dark .pagination__select:hover { border-color: #4b4e6b; }
.theme--dark .pager-btn {
  background: #22232e;
  border-color: #33354a;
  color: #e4e5eb;
}
.theme--dark .pager-btn:hover:not(:disabled) {
  background: #2a2c3a;
  border-color: #4b4e6b;
  color: #c7d2fe;
}
.theme--dark .pager-gap { color: #8a8fa3; }

/* ── activity table ── */
.theme--dark .activity-table th {
  background: #22232e;
  color: #8a8fa3;
}
.theme--dark .activity-table th,
.theme--dark .activity-table td { border-bottom-color: #2a2c3a; }
.theme--dark .activity-table td { color: #e4e5eb; }
.theme--dark .activity-table tr.clickable:hover td { background: #22232e; }
.theme--dark .activity-table .empty-row { color: #8a8fa3; }
.theme--dark .activity-table .error-cell { color: #f87171; }
.theme--dark .activity-table .col-arrow { color: #4b4e6b; }
.theme--dark .activity-table tr.is-selected td { background: rgba(99, 102, 241, .14); }
.theme--dark .activity-table tr.is-selected:hover td { background: rgba(99, 102, 241, .20); }

/* ── chips (event + status) ── */
.theme--dark .event-chip {
  background: #22232e;
  color: #d5d6de;
  border-color: rgba(99, 102, 241, .30);
}
.theme--dark .status-dot { box-shadow: 0 0 0 2px rgba(0, 0, 0, .35); }
.theme--dark .status--success { background: rgba(16, 185, 129, .14); color: #34d399; border-color: rgba(52, 211, 153, .28); }
.theme--dark .status--failed  { background: rgba(239, 68, 68, .14);  color: #f87171; border-color: rgba(248, 113, 113, .28); }
.theme--dark .status--pending { background: rgba(245, 158, 11, .14); color: #fbbf24; border-color: rgba(251, 191, 36, .28); }

/* ── confirm / resend dialog cards ── */
.theme--dark .confirm-card { background: #1a1b23 !important; }
.theme--dark .confirm-card .confirm-title { color: #e4e5eb; }
.theme--dark .confirm-card .confirm-body  { color: #8a8fa3; }

/* ── details dialog ── */
.theme--dark .details-dialog { background: #1a1b23 !important; }
.theme--dark .meta-grid {
  background: #22232e;
  border-color: #2a2c3a;
}
.theme--dark .meta-grid .meta-label { color: #8a8fa3; }
.theme--dark .meta-grid .meta-value { color: #e4e5eb; }
.theme--dark .block .block-title { color: #e4e5eb; }
.theme--dark .block .block-title--error { color: #f87171; }
.theme--dark .block .endpoint {
  background: #22232e;
  border-color: #33354a;
  color: #d5d6de;
}
.theme--dark .block .endpoint:hover {
  background: #2a2c3a;
  border-color: #4b4e6b;
}
.theme--dark .block .code--error {
  background: rgba(239, 68, 68, .10);
  color: #f87171;
}
</style>
